import {FlowItem, FlowNodeTypes, ProcessedNode} from "@/types/node";
import {FSMFlow, GlobalTrigger} from "./constants";
import {
  formatVariables,
  getActionsByType,
  getActionResults,
  makePreamble,
  getStatesFromNodes,
  getBookingVariables,
} from "./fsmUtils";
import {Variable} from "@/contexts/VariablesContext";
import {processNodes} from "@/contexts/SaveContext";
import {SubflowItem} from "@/contexts/SubflowsContext";
// Core

const getFsmFromFlow = (flow: FlowItem, variables: Variable[]) => {
  const processedNodes = processNodes(flow.nodes);
  const nodes = processedNodes.filter(
    node =>
      node.type !== FlowNodeTypes.PLACEHOLDER &&
      node.type !== FlowNodeTypes.ROOT,
  );
  const edges = flow.edges;

  const nodesByType = getActionsByType(nodes);

  const startNode = nodesByType[FlowNodeTypes.START][0] || nodes[1];
  const globalNode = nodesByType[FlowNodeTypes.GLOBAL][0] || null;
  const actionNodes = nodesByType[FlowNodeTypes.ACTION] || [];
  const endNodes =
    [...nodesByType[FlowNodeTypes.END]].map(node => node.id) || [];

  const actionResults = getActionResults(actionNodes);

  const actionVariables = formatVariables(actionResults);
  const flowVariables = formatVariables(variables);

  const states = getStatesFromNodes(
    nodes.filter(node => node.type !== FlowNodeTypes.GLOBAL),
    edges,
  );

  const result: FSMFlow = {
    version: 3,
    format: "finite_state_machine",
    name: "flow_name",
    initial_state: startNode?.id,
    final_states: endNodes,
    variables: {
      ...flowVariables,
      ...actionVariables,
      ...getBookingVariables(nodes as unknown as any[]),
    },
    states: states,
  };

  if (globalNode) {
    result.preamble = makePreamble(globalNode);
  }

  return result;
};
export const convertToFSM = (
  mainFlow: SubflowItem,
  subflows: SubflowItem[],
  syncVariablesWithNodes: (
    nodes: ProcessedNode[],
    subflowId: string,
    subflowName: string,
  ) => Variable[],
) => {
  const processedMainTestFlow = processNodes(mainFlow.flowData.test.nodes);
  const mainFlowVariables = syncVariablesWithNodes(
    processedMainTestFlow as unknown as ProcessedNode[],
    "main",
    "Main",
  );
  const mainFsm = getFsmFromFlow(mainFlow.flowData.test, mainFlowVariables);
  // Build global triggers from subflows' entry nodes
  const globalTriggers = subflows
    .map(flow => {
      const processedNodes = processNodes(flow.flowData.test.nodes);
      const entryNode = processedNodes.find(
        node => node.type === FlowNodeTypes.ENTRY,
      );

      if (!entryNode) return null;

      const triggersData = (entryNode.data.settings as any)?.triggers as Array<
        Array<{
          key: string;
          value: string;
        }>
      >;

      if (!triggersData || triggersData.length === 0) return null;

      const intents = triggersData
        .map(item => item[0]?.value)
        .filter(trigger => trigger && trigger.trim() !== "");

      if (intents.length === 0) return null;

      return {
        name: flow.name.toLowerCase().replace(/\s+/g, "_"),
        description: flow.description || flow.name,
        intents: intents,
        subflow: flow.id,
      };
    })
    .filter(Boolean) as GlobalTrigger[];

  const fsmData: FSMFlow = {
    ...mainFsm,
  };

  if (subflows.length > 0) {
    fsmData.subflows = {
      ...subflows.reduce((acc, flow) => {
        const processedSubflowTestFlow = processNodes(flow.flowData.test.nodes);
        const subflowVariables = syncVariablesWithNodes(
          processedSubflowTestFlow as unknown as ProcessedNode[],
          flow.id,
          flow.name,
        );
        const subflowFSM = getFsmFromFlow(flow.flowData.test, subflowVariables);

        acc[flow.id] = {
          definition: subflowFSM,
        };
        return acc;
      }, {}),
    };
    fsmData.global_triggers = globalTriggers;
  }
  return fsmData;
};
